import {
  Dispatch,
  FC,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { SocketClient } from 'app/hooks/socket';

import { WithChildren } from '../../../../theme/helpers';
import { LayoutSplashScreen } from '../../../../theme/layout/core';
import * as authHelper from './AuthHelpers';
import {
  getUserByToken,
  logout as logoutRequest,
  refresh as refreshRequest,
} from './requests';
import { AuthModel, UserModel } from './type';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

type AuthContextProps = {
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: UserModel | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
  logout: () => Promise<void>;
};

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {
    //do nothing
  },
  currentUser: undefined,
  setCurrentUser: () => {
    //do nothing
  },
  logout: async () => {
    //do nothing
  },
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>();

  const saveAuth = (newAuth: AuthModel | undefined) => {
    setAuth(newAuth);
    if (newAuth) {
      authHelper.setAuth(newAuth);
      // Socket.IO does not auto-reconnect after an auth-rejected connection (by design -
      // a rejected handshake is treated as a fatal error, not a transport blip). Nudge it
      // explicitly whenever we obtain a fresh token, covering both a brand-new login and
      // the bootstrap-time silent-refresh-on-reload path below in AuthInit.
      SocketClient.socket.disconnect();
      SocketClient.socket.connect();
    } else {
      authHelper.removeAuth();
    }
  };

  const logout = async () => {
    // Await this (best-effort) before clearing local state. saveAuth(undefined) below makes
    // auth.access_token go falsy, which makes AuthInit's effect immediately try a silent
    // /auth/refresh - if that lands before this revoke call finishes, the refresh cookie is
    // still valid and the user gets silently logged back in. Awaiting first closes that race:
    // by the time local state clears, the refresh token is already revoked server-side, so the
    // racing refresh attempt correctly fails instead of resurrecting the session.
    await logoutRequest().catch(() => undefined);
    saveAuth(undefined);
    setCurrentUser(undefined);
    useOrganizationStore.getState().clearOrganization();
  };

  return (
    <AuthContext.Provider value={{ auth, saveAuth, currentUser, setCurrentUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, saveAuth, logout, setCurrentUser } = useAuth();
  const didRequest = useRef(false);
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  // We should request user by authToken (IN OUR EXAMPLE IT'S access_token) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          const { data } = await getUserByToken(apiToken);
          if (data.data) {
            setCurrentUser(data.data);
            if (data.data?.organizationId)
              useOrganizationStore
                .getState()
                .setOrganization(
                  data.data?.organizationId,
                  data.data?.organizationName,
                  data.data?.organizationLogo,
                );
          }
        }
      } catch (error) {
        console.error(error);
        if (!didRequest.current) {
          logout();
        }
      } finally {
        setShowSplashScreen(false);
      }

      return () => (didRequest.current = true);
    };

    if (auth?.access_token) {
      requestUser(auth.access_token);
    } else {
      // No in-memory access token (e.g. after a full page reload or a new tab). Try to
      // silently restore the session from the httpOnly refresh cookie before giving up.
      const tryRestoreSession = async () => {
        try {
          const { data } = await refreshRequest();
          const token = data?.data?.access_token;
          if (token) {
            // Updates `auth`, which re-runs this effect and loads the user via requestUser.
            saveAuth({ access_token: token });
            return;
          }
        } catch {
          // fall through to logout
        }
        logout();
        setShowSplashScreen(false);
      };
      tryRestoreSession();
    }
    // eslint-disable-next-line
  }, [auth?.access_token]);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>;
};

export { AuthInit, AuthProvider, useAuth };
